import React, { useState, useEffect } from 'react';
import { X, Package, DollarSign, Hash, AlertCircle } from 'lucide-react';
import { rentalApi } from '../services/api';
import type { RentalItem, RentalItemCreate, RentalItemUpdate } from '../types';

interface RentalItemFormProps {
  isOpen: boolean;
  onClose: () => void;
  fairId: number;
  item?: RentalItem;
  onSuccess: () => void;
}

const RENTAL_CATEGORIES = [
  { value: 'furniture', label: 'Furniture', emoji: '🪑', examples: 'chairs, tables, stools, benches' },
  { value: 'lighting', label: 'Lighting', emoji: '💡', examples: 'LED lights, spotlights, string lights' },
  { value: 'audio', label: 'Audio', emoji: '🔊', examples: 'speakers, microphones, PA systems' },
  { value: 'display', label: 'Display', emoji: '🖼️', examples: 'easels, display racks, shelving' },
  { value: 'power', label: 'Power', emoji: '🔌', examples: 'extension cords, power strips, generators' },
  { value: 'tent', label: 'Tent/Canopy', emoji: '⛺', examples: 'pop-up tents, canopies, sidewalls' },
  { value: 'other', label: 'Other', emoji: '📦', examples: 'miscellaneous equipment' }
];

const RentalItemForm: React.FC<RentalItemFormProps> = ({
  isOpen,
  onClose,
  fairId,
  item,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('furniture');
  const [pricePerDay, setPricePerDay] = useState('');
  const [pricePerEvent, setPricePerEvent] = useState('');
  const [quantityAvailable, setQuantityAvailable] = useState('1');
  const [imageUrl, setImageUrl] = useState('');
  const [isActive, setIsActive] = useState(true);

  // Validation errors
  const [nameError, setNameError] = useState('');
  const [pricePerDayError, setPricePerDayError] = useState('');

  useEffect(() => {
    if (item) {
      // Load existing item data
      setName(item.name);
      setDescription(item.description || '');
      setCategory(item.category || 'furniture');
      setPricePerDay(item.price_per_day.toString());
      setPricePerEvent(item.price_per_event?.toString() || '');
      setQuantityAvailable(item.quantity_available.toString());
      setImageUrl(item.image_url || '');
      setIsActive(item.is_active);
    } else {
      // Reset for new item
      setName('');
      setDescription('');
      setCategory('furniture');
      setPricePerDay('');
      setPricePerEvent('');
      setQuantityAvailable('1');
      setImageUrl('');
      setIsActive(true);
    }
    setError(null);
    setNameError('');
    setPricePerDayError('');
  }, [item, isOpen]);

  const validateForm = (): boolean => {
    let isValid = true;

    // Name validation
    if (!name.trim()) {
      setNameError('Item name is required');
      isValid = false;
    } else if (name.length > 255) {
      setNameError('Name must be 255 characters or less');
      isValid = false;
    } else {
      setNameError('');
    }

    // Price per day validation
    if (!pricePerDay.trim()) {
      setPricePerDayError('Price per day is required');
      isValid = false;
    } else {
      const price = parseFloat(pricePerDay);
      if (isNaN(price) || price < 0) {
        setPricePerDayError('Price must be a valid positive number');
        isValid = false;
      } else {
        setPricePerDayError('');
      }
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data: RentalItemCreate | RentalItemUpdate = {
        name: name.trim(),
        description: description.trim() || undefined,
        category: category || undefined,
        price_per_day: parseFloat(pricePerDay),
        price_per_event: pricePerEvent.trim() ? parseFloat(pricePerEvent) : undefined,
        quantity_available: parseInt(quantityAvailable) || 1,
        image_url: imageUrl.trim() || undefined,
        is_active: isActive
      };

      if (item) {
        // Update existing item
        await rentalApi.updateRentalItem(item.id, data as RentalItemUpdate);
      } else {
        // Create new item
        await rentalApi.createRentalItem(fairId, data as RentalItemCreate);
      }

      onSuccess();
    } catch (err: unknown) {
      console.error('Failed to save rental item:', err);
      setError(err.response?.data?.detail || 'Failed to save rental item');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const selectedCategory = RENTAL_CATEGORIES.find(c => c.value === category);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Package className="w-6 h-6 text-secondary" />
            <h2 className="text-xl font-semibold text-gray-900">
              {item ? 'Edit Rental Equipment' : 'Add Rental Equipment'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-6 space-y-6">
          {/* Error Alert */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Item Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Item Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setNameError('');
              }}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                nameError ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="e.g., Folding Chair, 10x10 Pop-up Tent"
              maxLength={255}
            />
            {nameError && <p className="mt-1 text-sm text-red-600">{nameError}</p>}
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Optional: Provide details about this equipment..."
            />
          </div>

          {/* Category */}
          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              id="category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {RENTAL_CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>
                  {cat.emoji} {cat.label}
                </option>
              ))}
            </select>
            {selectedCategory && (
              <p className="mt-1 text-xs text-gray-500">
                Examples: {selectedCategory.examples}
              </p>
            )}
          </div>

          {/* Pricing */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="pricePerDay" className="block text-sm font-medium text-gray-700 mb-1">
                Price Per Day <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="w-4 h-4 text-gray-400" />
                </div>
                <input
                  type="number"
                  id="pricePerDay"
                  value={pricePerDay}
                  onChange={(e) => {
                    setPricePerDay(e.target.value);
                    setPricePerDayError('');
                  }}
                  step="0.01"
                  min="0"
                  className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                    pricePerDayError ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="0.00"
                />
              </div>
              {pricePerDayError && <p className="mt-1 text-sm text-red-600">{pricePerDayError}</p>}
              <p className="mt-1 text-xs text-gray-500">Daily rental rate</p>
            </div>

            <div>
              <label htmlFor="pricePerEvent" className="block text-sm font-medium text-gray-700 mb-1">
                Price Per Event
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="w-4 h-4 text-gray-400" />
                </div>
                <input
                  type="number"
                  id="pricePerEvent"
                  value={pricePerEvent}
                  onChange={(e) => setPricePerEvent(e.target.value)}
                  step="0.01"
                  min="0"
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="0.00"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">Optional: Flat rate for entire event</p>
            </div>
          </div>

          {/* Quantity */}
          <div>
            <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-1">
              Quantity Available
            </label>
            <div className="relative max-w-xs">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Hash className="w-4 h-4 text-gray-400" />
              </div>
              <input
                type="number"
                id="quantity"
                value={quantityAvailable}
                onChange={(e) => setQuantityAvailable(e.target.value)}
                min="0"
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="1"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Total number of units available for rental
            </p>
          </div>

          {/* Image URL */}
          <div>
            <label htmlFor="imageUrl" className="block text-sm font-medium text-gray-700 mb-1">
              Image URL
            </label>
            <input
              type="url"
              id="imageUrl"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="https://example.com/image.jpg"
            />
            <p className="mt-1 text-xs text-gray-500">
              Optional: URL to an image of this equipment
            </p>
          </div>

          {/* Active Status */}
          <div className="flex items-start space-x-3">
            <input
              type="checkbox"
              id="isActive"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="mt-1 rounded border-gray-300 text-secondary focus:ring-primary"
            />
            <div>
              <label htmlFor="isActive" className="text-sm font-medium text-gray-700 cursor-pointer">
                Active
              </label>
              <p className="text-xs text-gray-500 mt-0.5">
                Inactive items will not be shown to vendors
              </p>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary border border-transparent rounded-md hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : item ? 'Update Equipment' : 'Add Equipment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RentalItemForm;
